import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Fade,
  Grow,
  alpha,
  useTheme,
  TextField,
  Autocomplete,
  Skeleton,
  Button,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  CircularProgress,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import RouterIcon from '@mui/icons-material/Router';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import FirewallIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Router';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import BackupIcon from '@mui/icons-material/Backup';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AuthContext } from '../../AuthContext';
import { useApi } from '../../hooks/useApi';

function SonicWall() {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const jApi = useApi();
  const navigate = useNavigate();

  const [selectedAccount, setSelectedAccount] = useState();
  const [accounts, setAccounts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sonicwalls, setSonicwalls] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [backupData, setBackupData] = useState({});
  const [interfaceConfig, setInterfaceConfig] = useState(null);
  const [prtgDevices, setPrtgDevices] = useState([]);
  const [prtgSensors, setPrtgSensors] = useState({});
  const [capacityDrawerOpen, setCapacityDrawerOpen] = useState(false);
  const [capacityData, setCapacityData] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacitySensorId, setCapacitySensorId] = useState(null);
  const [capacityIngressKbps, setCapacityIngressKbps] = useState(100000);
  const [capacityEgressKbps, setCapacityEgressKbps] = useState(100000);

  const fetchData = async () => {
    try {
      setErrorMessage("");
      setIsLoading(true);
      const response = await jApi.request('GET', 'cma/account');

      const options = response.data
        .map(item => `${item.name} (${item.code})`)
        .sort((a, b) => a.localeCompare(b));

      setAccounts(options);

      let selected_account = sessionStorage.getItem('account');
      let selectedItem = options.find(option => option.includes("(" + selected_account + ")"));
      if (selectedItem) {
        setSelectedAccount(selectedItem);
      } else {
        setSelectedAccount(options[0]);
      }

      // Fetch SonicWALL data
      const accountCode = extractCodeFromLabel(selectedItem || options[0]);
      await fetchSonicWalls(accountCode);
    } catch (ex) {
      setErrorMessage(ex.toString());
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSonicWalls = async (accountCode) => {
    try {
      setIsLoading(true);
      const response = await jApi.request('GET', 'mysonicwall/devices');

      console.log('SonicWALL API Response:', response);
      console.log('Response data:', response.data);

      if (response.data && Array.isArray(response.data)) {
        let devices = response.data;
        console.log('Devices before filter:', devices);

        // Filter by account if specified
        if (accountCode) {
          devices = devices.filter(device => device.account === accountCode);
          console.log('Devices after filter for account', accountCode, ':', devices);
        }

        console.log('First device sample:', devices[0]);

        // Set initial list so cards appear
        setSonicwalls(devices);

        // Fetch detailed config and backup info for each device in parallel
        console.log('Fetching detailed configs for', devices.length, 'devices...');
        const detailPromises = devices.map(async device => {
          try {
            // Fetch device details
            const detailResponse = await jApi.request('GET', `mysonicwall/devices/${device.serialNumber}`);

            // Fetch backup data
            let lastBackup = null;
            try {
              const backupResponse = await jApi.request('GET', `mysonicwall/backups?serialnumber=${device.serialNumber}`);
              console.log(`Backup response for ${device.serialNumber}:`, backupResponse.data);
              if (backupResponse.data && Array.isArray(backupResponse.data) && backupResponse.data.length > 0) {
                // Sort by endTS descending to ensure most recent first
                const sortedBackups = backupResponse.data.sort((a, b) => b.endTS - a.endTS);
                lastBackup = sortedBackups[0];
                console.log(`Last backup for ${device.serialNumber}:`, lastBackup);
              } else {
                console.log(`No backups found for ${device.serialNumber}`);
              }
            } catch (backupError) {
              console.error(`Failed to fetch backup for ${device.serialNumber}:`, backupError);
            }

            return {
              ...device,
              details: detailResponse.data,
              lastBackup: lastBackup,
              // Extract key fields for easy access on cards
              firmware: detailResponse.data?.attributes?.firmware_version,
              liveStatus: detailResponse.data?.liveStatus,
              model: detailResponse.data?.attributes?.model,
              uptime: detailResponse.data?.attributes?.up_time,
            };
          } catch (error) {
            console.error(`Failed to fetch details for ${device.serialNumber}:`, error);
            return device; // Return original device if detail fetch fails
          }
        });

        // Wait for all detail fetches to complete
        const devicesWithDetails = await Promise.all(detailPromises);
        console.log('Devices with details loaded:', devicesWithDetails);

        // Update with detailed information
        setSonicwalls(devicesWithDetails);

        // Auto-select first device
        if (devicesWithDetails.length > 0) {
          setSelectedDevice(devicesWithDetails[0]);
          fetchInterfaceConfig(devicesWithDetails[0]);
          fetchPrtgDevices(accountCode, devicesWithDetails[0].serialNumber);
        } else {
          setSelectedDevice(null);
          setInterfaceConfig(null);
          setPrtgDevices([]);
        }
      } else {
        console.error('Unexpected response structure:', response);
        setSonicwalls([]);
        setSelectedDevice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SonicWALL data:', error);
      setErrorMessage('Failed to load SonicWALL data: ' + error.message);
      setSonicwalls([]);
      setSelectedDevice(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  function extractCodeFromLabel(label) {
    const match = label.match(/\((.*?)\)/);
    return match ? match[1] : null;
  }

  const handleOnChange = (event, newValue) => {
    if (!newValue) return;
    setSelectedAccount(newValue);
    const accountCode = extractCodeFromLabel(newValue);
    sessionStorage.setItem('account', accountCode);
    fetchSonicWalls(accountCode);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleBack = () => {
    navigate('/operation');
  };

  const fetchInterfaceConfig = async (device) => {
    if (!device.lastBackup || !device.lastBackup._id) {
      console.log('No backup available for interface config');
      setInterfaceConfig(null);
      return;
    }

    const backupId = device.lastBackup._id;
    console.log('Fetching interface config for backup_id:', backupId);

    try {
      const response = await jApi.request('GET', `mysonicwall/backups/config?id=${backupId}`);
      console.log('Interface config:', response.data);
      console.log('Security data:', response.data?.security);
      setInterfaceConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch interface config:', error);
      console.error('Error response:', error.response?.data);
      console.error('Backup ID used:', backupId);
      setInterfaceConfig(null);
    }
  };

  const fetchPrtgDevices = async (accountCode, deviceSerialNumber) => {
    console.log('Fetching PRTG data for account:', accountCode, 'device:', deviceSerialNumber);

    try {
      // Fetch devices to get IP addresses
      const deviceParams = new URLSearchParams({
        content: 'devices',
        id: '1',
        filter_group: `@sub([${accountCode}])`,
        columns: 'objid,name,host,icon'
      });

      const deviceResponse = await jApi.request('GET', `prtg/query?${deviceParams.toString()}`);
      console.log('PRTG devices (all):', deviceResponse.data);

      let devices = deviceResponse.data?.devices || deviceResponse.data || [];

      // Filter devices by serial number
      if (deviceSerialNumber && Array.isArray(devices)) {
        devices = devices.filter(device =>
          device.name && device.name.startsWith(deviceSerialNumber)
        );
        console.log('PRTG devices (filtered by serial):', devices);
      }

      setPrtgDevices(devices);

      // Fetch sensors
      const sensorParams = new URLSearchParams({
        content: 'sensors',
        id: '1',
        filter_group: `@sub([${accountCode}])`,
        columns: 'objid,name,status,group,probe,device,parentid'
      });

      const sensorResponse = await jApi.request('GET', `prtg/query?${sensorParams.toString()}`);
      console.log('PRTG sensors (all):', sensorResponse.data);

      let sensors = sensorResponse.data?.sensors || sensorResponse.data || [];

      // Filter sensors by device serial number
      if (deviceSerialNumber && Array.isArray(sensors)) {
        sensors = sensors.filter(sensor =>
          sensor.device && sensor.device.startsWith(deviceSerialNumber)
        );
        console.log('PRTG sensors (filtered by serial):', sensors);
      }

      // Create a device lookup map for IP addresses
      const deviceLookup = devices.reduce((acc, device) => {
        acc[device.name] = device;
        return acc;
      }, {});

      // Group sensors by device and add device info
      const groupedByDevice = sensors.reduce((acc, sensor) => {
        const deviceName = sensor.device || 'Unknown';
        if (!acc[deviceName]) {
          acc[deviceName] = {
            deviceInfo: deviceLookup[deviceName] || {},
            sensors: []
          };
        }
        acc[deviceName].sensors.push(sensor);
        return acc;
      }, {});

      console.log('PRTG sensors (grouped by device):', groupedByDevice);
      setPrtgSensors(groupedByDevice);
    } catch (error) {
      console.error('Failed to fetch PRTG data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error message:', error.response?.data?.error?.message);
      console.error('Request URL:', error.config?.url);
      setPrtgDevices([]);
      setPrtgSensors({});
    }
  };

  const handleDeviceChange = (event, newValue) => {
    if (newValue) {
      setSelectedDevice(newValue);
      fetchInterfaceConfig(newValue);
      const accountCode = extractCodeFromLabel(selectedAccount);
      fetchPrtgDevices(accountCode, newValue.serialNumber);
    }
  };

  const openCapacityDrawer = (sensorId, sensorName) => {
    // Try to find matching interface by sensor name (e.g., "(001) X0 (Portshield Host) Traffic" -> "X0")
    let ingressKbps = 100000; // Default 100Mbps
    let egressKbps = 100000;

    if (interfaceConfig?.interfaces && sensorName) {
      const interfaceMatch = sensorName.match(/\((\d+)\)\s+(\S+)/);
      if (interfaceMatch) {
        const interfaceName = interfaceMatch[2];
        const iface = interfaceConfig.interfaces.find(i => i.name === interfaceName);

        if (iface?.bandwidth?.ingress_enabled && iface?.bandwidth?.egress_enabled) {
          ingressKbps = iface.bandwidth.ingress_kbps;
          egressKbps = iface.bandwidth.egress_kbps;
          console.log(`Using bandwidth from interface ${interfaceName}:`, { ingressKbps, egressKbps });
        }
      }
    }

    setCapacitySensorId(sensorId);
    setCapacityIngressKbps(ingressKbps);
    setCapacityEgressKbps(egressKbps);
    setCapacityDrawerOpen(true);

    // Fetch initial data
    fetchCapacityData(sensorId, ingressKbps, egressKbps);
  };

  const fetchCapacityData = async (sensorId, ingressKbps, egressKbps) => {
    setCapacityLoading(true);
    setCapacityData(null);

    try {
      const params = new URLSearchParams({
        sensorid: sensorId,
        ingress_kbps: ingressKbps,
        egress_kbps: egressKbps,
        days_back: 30
      });

      const response = await jApi.request('GET', `prtg/networkcapacity?${params.toString()}`);
      console.log('Capacity data:', response.data);
      setCapacityData(response.data);
    } catch (error) {
      console.error('Failed to fetch capacity data:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setCapacityLoading(false);
    }
  };

  const handleRunCapacityAnalysis = () => {
    if (capacitySensorId) {
      fetchCapacityData(capacitySensorId, capacityIngressKbps, capacityEgressKbps);
    }
  };

  const getStatusChip = (liveStatus) => {
    if (liveStatus) {
      return (
        <Chip
          icon={<CheckCircleIcon fontSize="small" />}
          label="Online"
          size="small"
          sx={{
            borderRadius: 1,
            bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
            color: theme.palette.success.main,
            '& .MuiChip-icon': { color: theme.palette.success.main },
            border: `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`,
          }}
        />
      );
    } else {
      return (
        <Chip
          icon={<ErrorIcon fontSize="small" />}
          label="Offline"
          size="small"
          sx={{
            borderRadius: 1,
            bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
            color: theme.palette.error.main,
            '& .MuiChip-icon': { color: theme.palette.error.main },
            border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`,
          }}
        />
      );
    }
  };

  // Gradient header style
  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.background.paper, 0.8),
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeTwoToneIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.secondary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 500,
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Link to="/operation" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.main,
                    },
                  }}
                >
                  Operation Center
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                }}
              >
                SonicWALL Management
              </Typography>
            </Box>
          </Box>
        </Fade>

        {/* Hero Section with Account Switcher */}
        <Fade in timeout={600}>
          <Box
            sx={{
              ...headerGradient,
              p: 4,
              mb: 4,
            }}
          >
            <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
              {/* Title Section */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                      }}
                    >
                      <SecurityIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      SonicWALL Management
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Monitor and manage firewall devices
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Account Selector */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    {isLoading ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
                    ) : accounts && accounts.length > 0 ? (
                      <Autocomplete
                        options={accounts}
                        value={selectedAccount || null}
                        renderInput={(params) => (
                          <TextField {...params} label="Select Account" variant="outlined" />
                        )}
                        onChange={handleOnChange}
                        isOptionEqualToValue={(option, value) => option === value}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.paper, 0.8),
                            backdropFilter: 'blur(10px)',
                          },
                        }}
                      />
                    ) : (
                      <></>
                    )}
                  </Box>
                  <IconButton
                    onClick={handleBack}
                    sx={{
                      background: alpha(theme.palette.secondary.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.secondary.main, 0.2),
                      },
                    }}
                    title="Back to Operation Center"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{
                      background: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                    title="Refresh"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
          </Box>
        </Fade>

        {/* Error Message */}
        {errorMessage && (
          <Fade in>
            <Paper
              sx={{
                p: 3,
                mb: 3,
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="error.main" sx={{ fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            </Paper>
          </Fade>
        )}

        {/* No Data Message */}
        {!isLoading && sonicwalls.length === 0 && (
          <Fade in>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No SonicWALL devices found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No devices are currently configured for this account.
              </Typography>
            </Paper>
          </Fade>
        )}

        {/* Device Details - Inline Display */}
        {selectedDevice && selectedDevice.details && (
          <Fade in timeout={900}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                background: theme.palette.mode === 'dark'
                  ? theme.palette.background.paper
                  : '#ffffff',
              }}
            >
              <Stack spacing={2}>
                {/* Device Header */}
                <Box
                  sx={{
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                      }}
                    >
                      <RouterIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {sonicwalls.length > 1 ? (
                        <Autocomplete
                          fullWidth
                          options={sonicwalls}
                          value={selectedDevice}
                          onChange={handleDeviceChange}
                          getOptionLabel={(option) => option.friendlyName}
                          isOptionEqualToValue={(option, value) => option.serialNumber === value.serialNumber}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                sx: {
                                  fontSize: '1.5rem',
                                  fontWeight: 700,
                                  '& .MuiInputBase-input': {
                                    padding: 0,
                                  },
                                },
                              }}
                            />
                          )}
                          sx={{ mb: 0.5 }}
                        />
                      ) : (
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {selectedDevice.friendlyName}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label={selectedDevice.serialNumber}
                          size="small"
                          sx={{ fontFamily: 'monospace' }}
                        />
                        {selectedDevice.liveStatus !== undefined && getStatusChip(selectedDevice.liveStatus)}
                      </Stack>
                    </Box>
                  </Stack>
                </Box>

                {/* Device Information */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, px: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 20, color: 'info.main' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Device Information
                    </Typography>
                  </Stack>
                  <Box sx={{ px: 1 }}>
                    <Grid container spacing={2}>
                      {/* Device Info Column */}
                      <Grid size={{ xs: 12, md: 8 }}>
                        <Paper
                          sx={{
                            p: 2.5,
                            background: alpha(theme.palette.background.paper, 0.6),
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            borderRadius: 2,
                          }}
                        >
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Serial Number</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                              {selectedDevice.serialNumber}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Model</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {selectedDevice.details.attributes?.model || selectedDevice.details.modelInfo?.productName || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Current Firmware</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                              {selectedDevice.details.attributes?.firmware_version || 'N/A'}
                            </Typography>
                          </Grid>
                          {selectedDevice.details.ipAddress && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" color="text.secondary">IP Address</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                                {selectedDevice.details.ipAddress}
                              </Typography>
                            </Grid>
                          )}
                          {selectedDevice.details.location && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" color="text.secondary">Location</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {selectedDevice.details.location.city}, {selectedDevice.details.location.region}, {selectedDevice.details.location.countryName}
                              </Typography>
                            </Grid>
                          )}
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {getStatusChip(selectedDevice.details.liveStatus)}
                            </Box>
                          </Grid>
                          {selectedDevice.details.attributes?.up_time && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" color="text.secondary">Uptime</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {selectedDevice.details.attributes.up_time}
                              </Typography>
                            </Grid>
                          )}
                          {selectedDevice.details.synchronized !== undefined && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" color="text.secondary">Config Synchronized</Typography>
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={selectedDevice.details.synchronized ? 'Yes' : 'No'}
                                  size="small"
                                  color={selectedDevice.details.synchronized ? 'success' : 'warning'}
                                  variant="outlined"
                                />
                              </Box>
                            </Grid>
                          )}
                          {selectedDevice.details.attributes?.restart_required !== undefined && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <Typography variant="caption" color="text.secondary">Restart Required</Typography>
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={selectedDevice.details.attributes.restart_required ? 'Yes' : 'No'}
                                  size="small"
                                  color={selectedDevice.details.attributes.restart_required ? 'warning' : 'success'}
                                  variant="outlined"
                                />
                              </Box>
                            </Grid>
                          )}
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Last Backup</Typography>
                            <Paper
                              sx={{
                                p: 2,
                                background: selectedDevice.lastBackup
                                  ? (() => {
                                      const backupDate = new Date(selectedDevice.lastBackup.endDate);
                                      const today = new Date();
                                      const daysDiff = Math.floor((today - backupDate) / (1000 * 60 * 60 * 24));
                                      const isOld = daysDiff > 14;
                                      return `linear-gradient(135deg, ${alpha(isOld ? theme.palette.error.main : theme.palette.success.main, 0.08)} 0%, ${alpha(isOld ? theme.palette.error.main : theme.palette.success.main, 0.03)} 100%)`;
                                    })()
                                  : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.03)} 100%)`,
                                border: selectedDevice.lastBackup
                                  ? (() => {
                                      const backupDate = new Date(selectedDevice.lastBackup.endDate);
                                      const today = new Date();
                                      const daysDiff = Math.floor((today - backupDate) / (1000 * 60 * 60 * 24));
                                      const isOld = daysDiff > 14;
                                      return `1px solid ${alpha(isOld ? theme.palette.error.main : theme.palette.success.main, 0.3)}`;
                                    })()
                                  : `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                borderRadius: 2,
                              }}
                            >
                              {selectedDevice.lastBackup ? (
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 1.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: (() => {
                                        const backupDate = new Date(selectedDevice.lastBackup.endDate);
                                        const today = new Date();
                                        const daysDiff = Math.floor((today - backupDate) / (1000 * 60 * 60 * 24));
                                        const isOld = daysDiff > 14;
                                        return alpha(isOld ? theme.palette.error.main : theme.palette.success.main, 0.2);
                                      })(),
                                    }}
                                  >
                                    <BackupIcon
                                      sx={{
                                        fontSize: 28,
                                        color: (() => {
                                          const backupDate = new Date(selectedDevice.lastBackup.endDate);
                                          const today = new Date();
                                          const daysDiff = Math.floor((today - backupDate) / (1000 * 60 * 60 * 24));
                                          const isOld = daysDiff > 14;
                                          return isOld ? 'error.main' : 'success.main';
                                        })(),
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                                      {selectedDevice.lastBackup.path}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                      {selectedDevice.lastBackup.endDate}
                                    </Typography>
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                      {(() => {
                                        const backupDate = new Date(selectedDevice.lastBackup.endDate);
                                        const today = new Date();
                                        const daysDiff = Math.floor((today - backupDate) / (1000 * 60 * 60 * 24));
                                        const isOld = daysDiff > 14;

                                        return (
                                          <>
                                            <Chip
                                              label={selectedDevice.lastBackup.status}
                                              size="small"
                                              color={selectedDevice.lastBackup.status === 'success' && !isOld ? 'success' : 'warning'}
                                              sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.7rem' }}
                                            />
                                            {isOld && (
                                              <Chip
                                                icon={<WarningAmberIcon fontSize="small" />}
                                                label={`${daysDiff} days old`}
                                                size="small"
                                                color="error"
                                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                              />
                                            )}
                                            {selectedDevice.lastBackup.encrypted && (
                                              <Chip
                                                label="Encrypted"
                                                size="small"
                                                variant="outlined"
                                                color="success"
                                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                              />
                                            )}
                                          </>
                                        );
                                      })()}
                                    </Stack>
                                  </Box>
                                </Stack>
                              ) : (
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 1.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: alpha(theme.palette.error.main, 0.2),
                                    }}
                                  >
                                    <ErrorIcon sx={{ fontSize: 28, color: 'error.main' }} />
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                                      No Backups Found
                                    </Typography>
                                    <Typography variant="caption" color="error.main">
                                      No backup history available for this device
                                    </Typography>
                                  </Box>
                                </Stack>
                              )}
                            </Paper>
                          </Grid>
                          {interfaceConfig?.security && (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Admin MFA Status</Typography>
                              <Paper
                                sx={{
                                  p: 1.5,
                                  background: interfaceConfig.security.admin_mfa_enabled && interfaceConfig.security.admin_mfa_configured
                                    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`
                                    : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.03)} 100%)`,
                                  border: interfaceConfig.security.admin_mfa_enabled && interfaceConfig.security.admin_mfa_configured
                                    ? `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                    : `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                  borderRadius: 1.5,
                                }}
                              >
                                {interfaceConfig.security.admin_mfa_enabled && interfaceConfig.security.admin_mfa_configured ? (
                                  <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: alpha(theme.palette.success.main, 0.2),
                                      }}
                                    >
                                      <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                      Enabled & Configured
                                    </Typography>
                                  </Stack>
                                ) : interfaceConfig.security.admin_mfa_configured && !interfaceConfig.security.admin_mfa_enabled ? (
                                  <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: alpha(theme.palette.warning.main, 0.2),
                                      }}
                                    >
                                      <WarningAmberIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                        Configured but Not Enabled
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        MFA is set up but not enforced
                                      </Typography>
                                    </Box>
                                  </Stack>
                                ) : !interfaceConfig.security.admin_mfa_configured && interfaceConfig.security.admin_mfa_enabled ? (
                                  <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: alpha(theme.palette.error.main, 0.2),
                                      }}
                                    >
                                      <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                                        Enabled but Not Configured
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        MFA enabled but setup incomplete
                                      </Typography>
                                    </Box>
                                  </Stack>
                                ) : (
                                  <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: alpha(theme.palette.error.main, 0.2),
                                      }}
                                    >
                                      <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                                        Not Enabled or Configured
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        MFA is not set up
                                      </Typography>
                                    </Box>
                                  </Stack>
                                )}
                              </Paper>
                            </Grid>
                          )}
                          {interfaceConfig?.security && (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Web Interface Access</Typography>
                              <Paper
                                sx={{
                                  p: 2,
                                  background: interfaceConfig.security.web_interface_public
                                    ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.12)} 0%, ${alpha(theme.palette.error.main, 0.04)} 100%)`
                                    : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
                                  border: interfaceConfig.security.web_interface_public
                                    ? `2px solid ${alpha(theme.palette.error.main, 0.4)}`
                                    : `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                  borderRadius: 1.5,
                                }}
                              >
                                <Stack spacing={2}>
                                  {/* Public Access Warning */}
                                  {interfaceConfig.security.web_interface_public ? (
                                    <Box
                                      sx={{
                                        p: 1.5,
                                        background: alpha(theme.palette.error.main, 0.15),
                                        borderRadius: 1,
                                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                      }}
                                    >
                                      <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box
                                          sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: alpha(theme.palette.error.main, 0.3),
                                          }}
                                        >
                                          <WarningAmberIcon sx={{ fontSize: 20, color: 'error.main' }} />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main', mb: 0.25 }}>
                                            ⚠️ Management Interface Exposed to Internet
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Firewall management is accessible from public networks - high security risk
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </Box>
                                  ) : (
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                      <Box
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          background: alpha(theme.palette.success.main, 0.2),
                                        }}
                                      >
                                        <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                                      </Box>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                          Secure - Local Access Only
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Management interface not exposed to public networks
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  )}

                                  {/* Public Management Interfaces */}
                                  {interfaceConfig.security.public_management_interfaces && interfaceConfig.security.public_management_interfaces.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', mb: 1, display: 'block' }}>
                                        Public Management Interfaces
                                      </Typography>
                                      <Stack spacing={1}>
                                        {interfaceConfig.security.public_management_interfaces.map((iface, idx) => (
                                          <Paper
                                            key={idx}
                                            sx={{
                                              p: 1.5,
                                              background: alpha(theme.palette.error.main, 0.08),
                                              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                              borderRadius: 1,
                                            }}
                                          >
                                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                                              <Chip
                                                label={iface.name}
                                                size="small"
                                                sx={{
                                                  bgcolor: alpha(theme.palette.error.main, 0.2),
                                                  color: 'error.main',
                                                  fontWeight: 700,
                                                  fontFamily: 'monospace',
                                                }}
                                              />
                                              <Chip
                                                label={iface.zone}
                                                size="small"
                                                color="error"
                                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                              />
                                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                {iface.ip}
                                              </Typography>
                                              <Stack direction="row" spacing={0.5}>
                                                {iface.http && (
                                                  <Chip label="HTTP" size="small" color="error" sx={{ fontSize: '0.65rem', height: '20px', fontWeight: 600 }} />
                                                )}
                                                {iface.https && (
                                                  <Chip label="HTTPS" size="small" color="error" sx={{ fontSize: '0.65rem', height: '20px', fontWeight: 600 }} />
                                                )}
                                              </Stack>
                                            </Stack>
                                          </Paper>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}

                                  {/* Local Management Interfaces */}
                                  {interfaceConfig.security.local_management_interfaces && interfaceConfig.security.local_management_interfaces.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block' }}>
                                        Local Management Interfaces
                                      </Typography>
                                      <Stack spacing={1}>
                                        {interfaceConfig.security.local_management_interfaces.map((iface, idx) => (
                                          <Paper
                                            key={idx}
                                            sx={{
                                              p: 1.5,
                                              background: alpha(theme.palette.success.main, 0.05),
                                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                              borderRadius: 1,
                                            }}
                                          >
                                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                                              <Chip
                                                label={iface.name}
                                                size="small"
                                                sx={{
                                                  bgcolor: alpha(theme.palette.success.main, 0.15),
                                                  color: 'success.main',
                                                  fontWeight: 700,
                                                  fontFamily: 'monospace',
                                                }}
                                              />
                                              <Chip
                                                label={iface.zone}
                                                size="small"
                                                color="success"
                                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                              />
                                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                {iface.ip}
                                              </Typography>
                                              <Stack direction="row" spacing={0.5}>
                                                {iface.http && (
                                                  <Chip label="HTTP" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '20px', fontWeight: 600 }} />
                                                )}
                                                {iface.https && (
                                                  <Chip label="HTTPS" size="small" variant="outlined" color="success" sx={{ fontSize: '0.65rem', height: '20px', fontWeight: 600 }} />
                                                )}
                                              </Stack>
                                            </Stack>
                                          </Paper>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </Stack>
                              </Paper>
                            </Grid>
                          )}
                        </Grid>
                        </Paper>
                      </Grid>

                      {/* Support & Warranty Column */}
                      {selectedDevice.details.licenses3?.data?.supportServices && (
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Paper sx={{
                            p: 2.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            height: '100%',
                          }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                              <AdminPanelSettingsIcon sx={{ fontSize: 20, color: 'success.main' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Support & Warranty
                              </Typography>
                            </Stack>
                            <Stack spacing={1.5}>
                              {selectedDevice.details.licenses3.data.supportServices
                                .filter(service => service.status === 'licensed')
                                .map((service, idx) => (
                                  <Box key={idx}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {service.supportService}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                      <Chip
                                        label={service.status}
                                        size="small"
                                        color="success"
                                        sx={{ textTransform: 'capitalize' }}
                                      />
                                      {service.expiration && (
                                        <Typography variant="caption" color="text.secondary">
                                          Exp: {service.expiration}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                ))}
                            </Stack>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Network Interfaces */}
                {interfaceConfig && interfaceConfig.interfaces && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, px: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                        }}
                      >
                        <NetworkCheckIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Network Interfaces
                      </Typography>
                      <Chip
                        label={`${interfaceConfig.interfaces.filter(iface => iface.ip !== '0.0.0.0' || iface.zone).length} Configured`}
                        size="small"
                        sx={{
                          ml: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          fontWeight: 600,
                        }}
                      />
                    </Stack>
                    <Box sx={{ px: 1 }}>
                      <Stack spacing={2}>
                        {/* Primary WAN */}
                        {interfaceConfig.primary_wan && (
                          <Paper
                            sx={{
                              p: 2.5,
                              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.12)} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)`,
                              border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                              borderRadius: 2,
                              boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`,
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: alpha(theme.palette.info.main, 0.2),
                                }}
                              >
                                <RouterIcon sx={{ fontSize: 24, color: 'info.main' }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Chip
                                  label="Primary WAN"
                                  size="small"
                                  color="info"
                                  sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.7rem' }}
                                />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {interfaceConfig.primary_wan.name} {interfaceConfig.primary_wan.comment && `- ${interfaceConfig.primary_wan.comment}`}
                                </Typography>
                              </Box>
                            </Stack>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>IP Address</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, mt: 0.5 }}>
                                  {interfaceConfig.primary_wan.ip}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Gateway</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, mt: 0.5 }}>
                                  {interfaceConfig.primary_wan.gateway}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Config Type</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>{interfaceConfig.primary_wan.config_type}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Zone</Typography>
                                <Chip
                                  label={interfaceConfig.primary_wan.zone}
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    bgcolor: alpha(theme.palette.error.main, 0.15),
                                    color: 'error.main',
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        )}

                        {/* All Interfaces Table */}
                        <Paper
                          sx={{
                            overflow: 'hidden',
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            borderRadius: 2,
                          }}
                        >
                          <Box sx={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{
                                background: alpha(theme.palette.primary.main, 0.08),
                                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              }}>
                                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Interface</th>
                                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zone</th>
                                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>IP / Netmask</th>
                                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</th>
                                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bandwidth</th>
                                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Management</th>
                              </tr>
                            </thead>
                            <tbody>
                              {interfaceConfig.interfaces
                                .filter(iface => iface.ip !== '0.0.0.0' || iface.zone) // Show configured interfaces
                                .sort((a, b) => {
                                  // Extract base interface (e.g., "X0" from "X0:V3")
                                  const getBase = (name) => name.split(':')[0];
                                  const isVlan = (name) => name.includes(':');
                                  const isUInterface = (name) => getBase(name).startsWith('U');

                                  const baseA = getBase(a.name);
                                  const baseB = getBase(b.name);

                                  // U interfaces (like U0) go to the bottom
                                  if (isUInterface(a.name) && !isUInterface(b.name)) return 1;
                                  if (!isUInterface(a.name) && isUInterface(b.name)) return -1;

                                  // First, sort by base interface
                                  if (baseA !== baseB) {
                                    return baseA.localeCompare(baseB);
                                  }

                                  // If same base, parent interface comes before VLANs
                                  const aIsVlan = isVlan(a.name);
                                  const bIsVlan = isVlan(b.name);

                                  if (aIsVlan && !bIsVlan) return 1;
                                  if (!aIsVlan && bIsVlan) return -1;

                                  // Both are VLANs, sort by VLAN tag
                                  if (aIsVlan && bIsVlan) {
                                    const vlanA = parseInt(a.name.split(':V')[1]) || 0;
                                    const vlanB = parseInt(b.name.split(':V')[1]) || 0;
                                    return vlanA - vlanB;
                                  }

                                  return 0;
                                })
                                .map((iface, idx) => (
                                  <tr
                                    key={idx}
                                    style={{
                                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                      transition: 'background-color 0.2s',
                                      cursor: 'default',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = alpha(theme.palette.primary.main, 0.04)}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    <td style={{ padding: '12px 16px' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {iface.name}
                                      </Typography>
                                      {iface.comment && (
                                        <Typography variant="caption" color="text.secondary">
                                          {iface.comment}
                                        </Typography>
                                      )}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                      {iface.zone && (
                                        <Chip
                                          label={iface.zone}
                                          size="small"
                                          sx={{
                                            bgcolor: iface.zone === 'WAN' ? alpha(theme.palette.error.main, 0.1) :
                                                     iface.zone === 'LAN' ? alpha(theme.palette.success.main, 0.1) :
                                                     alpha(theme.palette.info.main, 0.1),
                                            color: iface.zone === 'WAN' ? 'error.main' :
                                                   iface.zone === 'LAN' ? 'success.main' :
                                                   'info.main',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                          }}
                                        />
                                      )}
                                      {!iface.zone && <Typography variant="body2" color="text.secondary">-</Typography>}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                        {iface.ip} / {iface.netmask}
                                      </Typography>
                                      {iface.gateway !== '0.0.0.0' && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                          GW: {iface.gateway}
                                        </Typography>
                                      )}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                      <Typography variant="body2">{iface.config_type}</Typography>
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                      {iface.bandwidth && iface.bandwidth.ingress_enabled && iface.bandwidth.egress_enabled ? (
                                        <Box>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                            ↓ {iface.bandwidth.ingress_kbps.toLocaleString()} Kbps
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                            ↑ {iface.bandwidth.egress_kbps.toLocaleString()} Kbps
                                          </Typography>
                                        </Box>
                                      ) : (iface.zone === 'WAN' && !iface.name.startsWith('U')) ? (
                                        <Chip
                                          label={!iface.bandwidth ? "Not Set" : "Not Configured"}
                                          size="small"
                                          color="error"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">-</Typography>
                                      )}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                        {iface.management.ping && (
                                          <Chip label="Ping" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '22px', fontWeight: 600 }} />
                                        )}
                                        {iface.management.http && (
                                          <Chip label="HTTP" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '22px', fontWeight: 600 }} />
                                        )}
                                        {iface.management.https && (
                                          <Chip label="HTTPS" size="small" variant="outlined" color="success" sx={{ fontSize: '0.65rem', height: '22px', fontWeight: 600 }} />
                                        )}
                                        {iface.management.ssh && (
                                          <Chip label="SSH" size="small" variant="outlined" color="primary" sx={{ fontSize: '0.65rem', height: '22px', fontWeight: 600 }} />
                                        )}
                                        {iface.management.snmp && (
                                          <Chip label="SNMP" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '22px', fontWeight: 600 }} />
                                        )}
                                        {!Object.values(iface.management).some(v => v) && (
                                          <Typography variant="caption" color="text.secondary">None</Typography>
                                        )}
                                      </Stack>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          </Box>
                        </Paper>
                      </Stack>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Failover */}
                {interfaceConfig?.load_balancing && interfaceConfig.load_balancing.groups && interfaceConfig.load_balancing.groups.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, px: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.2)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`,
                        }}
                      >
                        <TrendingUpIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        WAN Failover
                      </Typography>
                    </Stack>
                    <Box sx={{ px: 1 }}>
                      <Stack spacing={2}>
                        {interfaceConfig.load_balancing.groups.map((group, idx) => {
                          const members = interfaceConfig.load_balancing.members.filter(
                            m => m.group_name === group.name
                          ).sort((a, b) => a.admin_rank - b.admin_rank);

                          return (
                            <Box key={idx}>
                              {members.length > 0 && (
                                <Paper
                                  sx={{
                                    overflow: 'hidden',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    borderRadius: 2,
                                  }}
                                >
                                  <Box sx={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{
                                          background: alpha(theme.palette.warning.main, 0.08),
                                          borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                        }}>
                                          <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', width: '70%' }}>Interface</th>
                                          <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', width: '30%' }}>Priority Rank</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {members.map((member, mIdx) => (
                                          <tr
                                            key={mIdx}
                                            style={{
                                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                              transition: 'background-color 0.2s',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = alpha(theme.palette.warning.main, 0.04)}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                          >
                                            <td style={{ padding: '12px 16px' }}>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {member.interface_name}
                                              </Typography>
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>
                                              <Chip
                                                label={`Priority ${member.admin_rank}`}
                                                size="small"
                                                sx={{
                                                  bgcolor: member.admin_rank === 1
                                                    ? alpha(theme.palette.success.main, 0.15)
                                                    : alpha(theme.palette.warning.main, 0.15),
                                                  color: member.admin_rank === 1 ? 'success.main' : 'warning.main',
                                                  fontWeight: 600,
                                                  fontSize: '0.7rem',
                                                }}
                                              />
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </Box>
                                </Paper>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Monitoring */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, px: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                      }}
                    >
                      <TrendingUpIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Monitoring (PRTG)
                    </Typography>
                  </Stack>
                  <Box sx={{ px: 1 }}>
                    {prtgSensors && Object.keys(prtgSensors).length > 0 ? (
                      <Stack spacing={3}>
                        {Object.entries(prtgSensors).map(([deviceName, deviceData]) => (
                          <Paper
                            key={deviceName}
                            sx={{
                              overflow: 'hidden',
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              borderRadius: 2,
                            }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: deviceData.deviceInfo?.icon === 'Device_Firewall.png'
                                      ? alpha(theme.palette.error.main, 0.15)
                                      : alpha(theme.palette.primary.main, 0.15),
                                  }}
                                >
                                  {deviceData.deviceInfo?.icon === 'Device_Firewall.png' ? (
                                    <FirewallIcon sx={{ fontSize: 24, color: 'error.main' }} />
                                  ) : (
                                    <DevicesIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                                  )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {deviceName}
                                  </Typography>
                                  {deviceData.deviceInfo?.host && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                      {deviceData.deviceInfo.host}
                                    </Typography>
                                  )}
                                </Box>
                                <Chip
                                  label={`${deviceData.sensors.length} sensors`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.secondary.main, 0.15),
                                    color: 'secondary.main',
                                    fontWeight: 600,
                                  }}
                                />
                              </Stack>
                            </Box>
                            <Box sx={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                <colgroup>
                                  <col style={{ width: '35%' }} />
                                  <col style={{ width: '12%' }} />
                                  <col style={{ width: '23%' }} />
                                  <col style={{ width: '18%' }} />
                                  <col style={{ width: '12%' }} />
                                </colgroup>
                                <thead>
                                  <tr style={{
                                    background: alpha(theme.palette.background.default, 0.4),
                                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                                  }}>
                                    <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sensor</th>
                                    <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                                    <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Group</th>
                                    <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Host</th>
                                    <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deviceData.sensors.map((sensor, idx) => (
                                    <tr
                                      key={sensor.objid || idx}
                                      style={{
                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                        transition: 'background-color 0.2s',
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = alpha(theme.palette.secondary.main, 0.04)}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      <td style={{ padding: '12px 16px' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                          {sensor.name}
                                        </Typography>
                                      </td>
                                      <td style={{ padding: '12px 16px' }}>
                                        <Chip
                                          label={sensor.status}
                                          size="small"
                                          color={sensor.status === 'Up' ? 'success' : sensor.status === 'Down' ? 'error' : 'warning'}
                                          sx={{ fontSize: '0.7rem', height: '24px', fontWeight: 600 }}
                                        />
                                      </td>
                                      <td style={{ padding: '12px 16px' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{sensor.group || '-'}</Typography>
                                      </td>
                                      <td style={{ padding: '12px 16px' }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.825rem' }}>
                                          {deviceData.deviceInfo?.host || '-'}
                                        </Typography>
                                      </td>
                                      <td style={{ padding: '12px 16px' }}>
                                        {sensor.name && sensor.name.toLowerCase().includes('traffic') && (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => openCapacityDrawer(sensor.objid, sensor.name)}
                                            sx={{
                                              fontSize: '0.7rem',
                                              py: 0.5,
                                              px: 1.5,
                                              fontWeight: 600,
                                              boxShadow: 'none',
                                              '&:hover': {
                                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                                              },
                                            }}
                                          >
                                            Measure
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Paper
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.03)} 100%)`,
                          border: `2px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
                          borderRadius: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: alpha(theme.palette.warning.main, 0.15),
                            margin: '0 auto',
                            mb: 2,
                          }}
                        >
                          <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                        </Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'warning.main' }}>
                          Monitoring Not Configured
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, margin: '0 auto' }}>
                          No PRTG monitoring devices or sensors have been configured for this firewall. Configure monitoring to track network performance and capacity.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Available Firmware Versions */}
                {selectedDevice.details.availableVersions && selectedDevice.details.availableVersions.length > 0 && (
                  <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Available Firmware Versions ({selectedDevice.details.availableVersions.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {selectedDevice.details.availableVersions.slice(0, 5).map((version, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {version.version}
                                </Typography>
                                <Chip label={version.releaseType} size="small" variant="outlined" />
                              </Stack>
                            }
                            secondary={`Released: ${version.releaseDate}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
                )}

                {/* Security Services */}
                {selectedDevice.details.licenses3?.data?.securityServices && (
                  <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Security Services
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {selectedDevice.details.licenses3.data.securityServices
                          .filter(service => service.status === 'licensed')
                          .map((service, idx) => (
                            <ListItem key={idx} sx={{ px: 0 }}>
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {service.securityService}
                                    </Typography>
                                    <Chip
                                      label={service.status}
                                      size="small"
                                      color={service.status === 'expired' ? 'error' : 'success'}
                                      sx={{ textTransform: 'capitalize' }}
                                    />
                                  </Stack>
                                }
                                secondary={service.expiration ? `Expires: ${service.expiration}` : 'No expiration'}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Stack>
            </Paper>
          </Fade>
        )}
      </Container>

      {/* Capacity Analysis Drawer */}
      <Drawer
        anchor="right"
        open={capacityDrawerOpen}
        onClose={() => setCapacityDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600 },
            p: 3,
          },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Network Capacity Analysis
            </Typography>
            <Stack direction="row" spacing={1}>
              {capacitySensorId && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => window.open(`https://joyous-goose.my-prtg.com/sensor.htm?id=${capacitySensorId}&tabid=1`, '_blank')}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  View in PRTG
                </Button>
              )}
              <IconButton onClick={() => setCapacityDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Divider />

          {/* Capacity Inputs */}
          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Bandwidth Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Ingress (Download) Kbps"
                  type="number"
                  value={capacityIngressKbps}
                  onChange={(e) => setCapacityIngressKbps(parseInt(e.target.value) || 0)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Egress (Upload) Kbps"
                  type="number"
                  value={capacityEgressKbps}
                  onChange={(e) => setCapacityEgressKbps(parseInt(e.target.value) || 0)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleRunCapacityAnalysis}
                  disabled={capacityLoading}
                >
                  {capacityLoading ? 'Analyzing...' : 'Run Analysis'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {capacityLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : capacityData ? (
            <Stack spacing={3}>
              {/* Analysis Period */}
              <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Analysis Period
                </Typography>
                <Typography variant="body2">
                  {capacityData.analysis_period_days} days ({capacityData.start_date} to {capacityData.end_date})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {capacityData.sample_count} samples analyzed
                </Typography>
              </Paper>

              {/* Overall Status */}
              <Paper sx={{ p: 2, bgcolor: alpha(capacityData.overall_status.pass ? theme.palette.success.main : theme.palette.error.main, 0.1) }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {capacityData.overall_status.pass ? (
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                  ) : (
                    <ErrorIcon sx={{ color: 'error.main', fontSize: 28 }} />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {capacityData.overall_status.pass ? 'Capacity: PASS' : 'Capacity: FAIL'}
                  </Typography>
                </Stack>
              </Paper>

              {/* Ingress Stats */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Ingress (Download)
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Capacity Limit:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(capacityData.capacity_limits.ingress_kbps / 1000).toFixed(0)} Mbps
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Average:</Typography>
                    <Typography variant="body2">
                      {(capacityData.ingress.avg_kbps / 1000).toFixed(2)} Mbps ({capacityData.ingress.avg_utilization_pct}%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">95th Percentile:</Typography>
                    <Typography variant="body2">
                      {(capacityData.ingress.p95_kbps / 1000).toFixed(2)} Mbps ({capacityData.ingress.p95_utilization_pct}%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Peak:</Typography>
                    <Typography variant="body2">
                      {(capacityData.ingress.peak_kbps / 1000).toFixed(2)} Mbps ({capacityData.ingress.peak_utilization_pct}%)
                    </Typography>
                  </Box>
                  <Chip
                    label={capacityData.ingress.status.replace(/_/g, ' ')}
                    color={
                      capacityData.ingress.status === 'OK' ? 'success' :
                      capacityData.ingress.status === 'OVER_50_PEAK_ONLY' ? 'warning' :
                      'error'
                    }
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  />
                  {capacityData.ingress.status === 'OVER_50_PEAK_ONLY' && (
                    <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.1), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', display: 'block', mb: 0.5 }}>
                        WARNING - Occasional spikes during busy periods
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • P95 is under 50%, but peak exceeded 50%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • 95% of the time traffic is fine
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • Brief bursts go over 50%
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', display: 'block', mt: 0.5 }}>
                        Action: Monitor and plan for future upgrade
                      </Typography>
                    </Paper>
                  )}
                  {capacityData.ingress.status.includes('OVER') && capacityData.ingress.status !== 'OVER_50_PEAK_ONLY' && capacityData.ingress.p95_utilization_pct > 50 && (
                    <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.error.main, 0.1), border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main', display: 'block', mb: 0.5 }}>
                        FAIL - Consistently high usage
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • P95 utilization is over 50%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • More than 5% of the time you're over 50%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • Circuit is regularly congested
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main', display: 'block', mt: 0.5 }}>
                        Action: Upgrade bandwidth NOW
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </Box>

              {/* Egress Stats */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Egress (Upload)
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Capacity Limit:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {(capacityData.capacity_limits.egress_kbps / 1000).toFixed(0)} Mbps
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Average:</Typography>
                    <Typography variant="body2">
                      {(capacityData.egress.avg_kbps / 1000).toFixed(2)} Mbps ({capacityData.egress.avg_utilization_pct}%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">95th Percentile:</Typography>
                    <Typography variant="body2">
                      {(capacityData.egress.p95_kbps / 1000).toFixed(2)} Mbps ({capacityData.egress.p95_utilization_pct}%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Peak:</Typography>
                    <Typography variant="body2">
                      {(capacityData.egress.peak_kbps / 1000).toFixed(2)} Mbps ({capacityData.egress.peak_utilization_pct}%)
                    </Typography>
                  </Box>
                  <Chip
                    label={capacityData.egress.status.replace(/_/g, ' ')}
                    color={
                      capacityData.egress.status === 'OK' ? 'success' :
                      capacityData.egress.status === 'OVER_50_PEAK_ONLY' ? 'warning' :
                      'error'
                    }
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  />
                  {capacityData.egress.status === 'OVER_50_PEAK_ONLY' && (
                    <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.1), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', display: 'block', mb: 0.5 }}>
                        WARNING - Occasional spikes during busy periods
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • P95 is under 50%, but peak exceeded 50%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • 95% of the time traffic is fine
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • Brief bursts go over 50%
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', display: 'block', mt: 0.5 }}>
                        Action: Monitor and plan for future upgrade
                      </Typography>
                    </Paper>
                  )}
                  {capacityData.egress.status.includes('OVER') && capacityData.egress.status !== 'OVER_50_PEAK_ONLY' && capacityData.egress.p95_utilization_pct > 50 && (
                    <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.error.main, 0.1), border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main', display: 'block', mb: 0.5 }}>
                        FAIL - Consistently high usage
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • P95 utilization is over 50%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • More than 5% of the time you're over 50%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        • Circuit is regularly congested
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main', display: 'block', mt: 0.5 }}>
                        Action: Upgrade bandwidth NOW
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </Stack>
      </Drawer>
    </Box>
  );
}

export default SonicWall;
